import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Loader2,
  ArrowRight,
  RefreshCw,
  Globe,
  FlaskConical,
  Stethoscope,
  Rocket,
  TrendingUp,
  TrendingDown,
  Minus,
  AlertTriangle,
  Lightbulb,
  Eye,
} from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const BUBBLE_META = [
  { key: "market",       label: "Market",              icon: Globe,        path: "/strategy/market",     accent: "cyan" },
  { key: "science",      label: "Science",             icon: FlaskConical, path: "/strategy/technology", accent: "violet" },
  { key: "clinical_reg", label: "Clinical & Regulatory", icon: Stethoscope,path: "/strategy/clinical",   accent: "emerald" },
  { key: "fundraising",  label: "Fundraising",         icon: Rocket,       path: "/fundraising/preparation", accent: "amber" },
];

const KIND_META = {
  opportunity: { label: "Opportunity", dot: "bg-emerald-500", text: "text-emerald-600" },
  watch:       { label: "Watch",       dot: "bg-amber-500",   text: "text-amber-600" },
  risk:        { label: "Risk",        dot: "bg-rose-500",    text: "text-rose-600" },
};

export default function Dashboard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [data, setData] = useState(null);

  useEffect(() => {
    (async () => {
      // First, gate: if profile isn't completed, send to Profile.
      try {
        const pr = await fetch(`${BACKEND_URL}/api/company-profile`, { credentials: "include" });
        if (pr.ok) {
          const pd = await pr.json();
          if (!pd?.profile?.profile_completed) {
            navigate("/profile", { replace: true });
            return;
          }
        }
      } catch {}
      await load();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/dashboard/signals`, { credentials: "include" });
      if (!res.ok) throw new Error("failed");
      setData(await res.json());
    } catch {
      toast.error("Could not load dashboard signals");
    } finally {
      setLoading(false);
    }
  };

  const refresh = async () => {
    setRefreshing(true);
    try {
      // Best-effort refresh PubMed then reload signals
      await fetch(`${BACKEND_URL}/api/science-news/refresh`, { method: "POST", credentials: "include" });
    } catch {}
    await load();
    setRefreshing(false);
    toast.success("Signals refreshed");
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="w-8 h-8 animate-spin text-teal-500" />
      </div>
    );
  }

  const counts = data?.counts || { risks: 0, opportunities: 0, watchouts: 0 };
  const bubbles = data?.bubbles || {};
  const s = data?.synthesis || {};

  const MomentumIcon =
    s.momentum === "Positive" ? TrendingUp :
    s.momentum === "Negative" ? TrendingDown : Minus;
  const momentumTone =
    s.momentum === "Positive" ? "text-emerald-600" :
    s.momentum === "Negative" ? "text-rose-600" : "text-slate-500";

  return (
    <div className="max-w-6xl mx-auto space-y-8" data-testid="dashboard-v2">
      {/* Layer 1 — What changed? */}
      <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm" data-testid="what-changed-strip">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <div className="text-[10px] font-mono text-slate-500 tracking-[0.25em] uppercase mb-2">
              What changed · {data?.since || "since your last visit"}
            </div>
            <div className="flex flex-wrap gap-x-6 gap-y-2 mb-3 text-lg font-semibold text-slate-900">
              <span data-testid="count-risks"><span className="text-rose-600">{counts.risks}</span> Risks</span>
              <span className="text-slate-300">·</span>
              <span data-testid="count-opps"><span className="text-emerald-600">{counts.opportunities}</span> Opportunities</span>
              <span className="text-slate-300">·</span>
              <span data-testid="count-watches"><span className="text-amber-600">{counts.watchouts}</span> Watchouts</span>
            </div>
            <p className="text-slate-700 leading-relaxed max-w-3xl">
              {data?.headline || "Not enough data yet — upload documents and refresh feeds to see live signals."}
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={refresh}
            disabled={refreshing}
            data-testid="btn-refresh-signals"
            className="shrink-0"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        </div>
      </section>

      {/* Layer 2 — 4 signal bubbles */}
      <section>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="signal-bubbles">
          {BUBBLE_META.map((b) => {
            const Icon = b.icon;
            const items = bubbles[b.key] || [];
            return (
              <button
                key={b.key}
                onClick={() => navigate(b.path)}
                data-testid={`bubble-${b.key}`}
                className="text-left rounded-2xl border border-slate-200 bg-white p-6 hover:border-teal-400 hover:shadow-md transition-all group"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className={`w-10 h-10 rounded-xl bg-${b.accent}-100 flex items-center justify-center`}>
                    <Icon className={`w-5 h-5 text-${b.accent}-600`} />
                  </div>
                  <h3 className="text-lg font-bold text-slate-900 flex-1">{b.label}</h3>
                  <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-teal-600 transition-colors" />
                </div>
                {items.length === 0 ? (
                  <div className="text-sm text-slate-400 italic py-6 text-center border border-dashed border-slate-200 rounded-lg">
                    No signals yet — click Refresh above.
                  </div>
                ) : (
                  <div className="space-y-3">
                    {items.slice(0, 3).map((sig, i) => {
                      const k = KIND_META[sig.kind] || KIND_META.watch;
                      return (
                        <div key={i} className="rounded-lg border border-slate-100 bg-slate-50/60 p-3">
                          <div className="flex items-start gap-2">
                            <span className={`inline-block w-2 h-2 rounded-full mt-1.5 shrink-0 ${k.dot}`} />
                            <div className="flex-1">
                              <div className={`text-[10px] font-semibold uppercase tracking-wider mb-0.5 ${k.text}`}>
                                {k.label}
                              </div>
                              <div className="text-sm font-medium text-slate-900 leading-snug">
                                {sig.title}
                              </div>
                              {sig.so_what && (
                                <div className="text-xs text-slate-600 mt-1.5">
                                  <span className="font-semibold text-slate-500">What it means for you: </span>
                                  {sig.so_what}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </section>

      {/* Layer 3 — Operon's current view */}
      <section className="rounded-2xl border border-slate-900 bg-slate-900 text-white p-6" data-testid="operon-view">
        <div className="flex items-center justify-between mb-5">
          <div className="text-[10px] font-mono tracking-[0.25em] uppercase text-teal-300">
            Operon&apos;s current view
          </div>
          <div className={`flex items-center gap-2 text-sm font-medium ${momentumTone.replace('text-', 'text-')}`}>
            <MomentumIcon className={`w-4 h-4 ${momentumTone}`} />
            <span className="text-slate-100">Momentum:</span>
            <span className={momentumTone}>{s.momentum || "Neutral"}</span>
          </div>
        </div>
        <div className="grid md:grid-cols-3 gap-4">
          <SynthCard icon={Lightbulb} tone="emerald" title="Biggest opportunity" value={s.biggest_opportunity} />
          <SynthCard icon={AlertTriangle} tone="rose" title="Biggest risk" value={s.biggest_risk} />
          <SynthCard icon={Eye} tone="amber" title="Watch closely" value={s.watch_closely} />
        </div>
        <div className="mt-6 flex justify-end">
          <Button
            onClick={() => navigate("/strategy/market?tab=markets")}
            className="bg-teal-500 hover:bg-teal-400 text-slate-950 font-semibold"
            data-testid="btn-explore-strategy"
          >
            {s.cta || "Explore what this means for your strategy"}
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </section>
    </div>
  );
}

function SynthCard({ icon: Icon, tone, title, value }) {
  const bg = { emerald: "bg-emerald-500/10 border-emerald-500/30", rose: "bg-rose-500/10 border-rose-500/30", amber: "bg-amber-500/10 border-amber-500/30" }[tone];
  const ic = { emerald: "text-emerald-400", rose: "text-rose-400", amber: "text-amber-400" }[tone];
  return (
    <div className={`rounded-xl border p-4 ${bg}`}>
      <div className="flex items-center gap-2 mb-2">
        <Icon className={`w-4 h-4 ${ic}`} />
        <div className="text-[10px] font-mono tracking-widest uppercase text-slate-400">{title}</div>
      </div>
      <div className="text-sm text-slate-100 leading-relaxed">
        {value || "—"}
      </div>
    </div>
  );
}
